import { useState, useMemo, Fragment, type FormEvent } from 'react';
import { getStaff, saveStaff, type StaffMember } from '@/utils/dataStore';
import { ROLES, ROLE_ORDER, mergePermissions, type RoleKey, type Permission } from '@/utils/rbac';
import { useRBAC } from '@/hooks/useRBAC';
import NoPermission from '@/components/base/NoPermission';

const DEPT_OPTIONS = ['技术部', '活动运营部', '内容运营部', '审核部', '市场部', '财务部', '国际合作部'];

// Permission matrix row definition
interface PermRow {
  label: string;
  icon: string;
  perms: Partial<Record<'view' | 'write' | 'delete' | 'approve', Permission>>;
}

const PERM_ROWS: PermRow[] = [
  { label: '数据总览', icon: 'ri-dashboard-3-line', perms: { view: 'dashboard:view' } },
  { label: '活动管理', icon: 'ri-calendar-event-line', perms: { view: 'events:view', write: 'events:write', delete: 'events:delete' } },
  { label: '活动报名', icon: 'ri-calendar-check-line', perms: { view: 'registrations:view', delete: 'registrations:delete', approve: 'registrations:approve' } },
  { label: '合作申请', icon: 'ri-file-list-3-line', perms: { view: 'applications:view', delete: 'applications:delete', approve: 'applications:approve' } },
  { label: '嘉宾管理', icon: 'ri-user-star-line', perms: { view: 'guests:view', write: 'guests:write', delete: 'guests:delete' } },
  { label: '捐赠人', icon: 'ri-heart-line', perms: { view: 'donors:view', write: 'donors:write', delete: 'donors:delete' } },
  { label: '媒体报道', icon: 'ri-newspaper-line', perms: { view: 'media:view', write: 'media:write', delete: 'media:delete' } },
  { label: '工作人员', icon: 'ri-team-line', perms: { view: 'staff:view', write: 'staff:write', delete: 'staff:delete' } },
];

const COL_LABELS: Record<'view' | 'write' | 'delete' | 'approve', string> = {
  view: '查看',
  write: '添加/编辑',
  delete: '删除',
  approve: '审批',
};

interface FormState {
  name: string;
  email: string;
  password: string;
  roles: RoleKey[];
  permissions: Permission[];
  status: 'active' | 'inactive';
  department: string;
}

const BLANK: FormState = {
  name: '', email: '', password: 'gtra2026',
  roles: ['viewer'], permissions: [...mergePermissions(['viewer'])],
  status: 'active', department: '技术部',
};

export default function AdminStaffPage() {
  const { can, isSuperAdmin } = useRBAC();
  const canView = can('staff:view');
  const [staff, setStaff] = useState<StaffMember[]>(() => getStaff());
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<StaffMember | null>(null);
  const [form, setForm] = useState<FormState>(BLANK);
  const [toast, setToast] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showPasswords, setShowPasswords] = useState<Set<string>>(new Set());
  const [expandedPermId, setExpandedPermId] = useState<string | null>(null);

  const getEffectivePerms = (s: StaffMember): Permission[] => {
    if (s.roles.includes('super_admin')) return mergePermissions(['super_admin']);
    if (s.permissions && s.permissions.length > 0) return s.permissions;
    return mergePermissions(s.roles);
  };

  const filtered = useMemo(() => staff.filter((s) => {
    if (filterRole !== 'all' && !s.roles.includes(filterRole as RoleKey)) return false;
    if (search && !s.name.includes(search) && !s.email.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [staff, search, filterRole]);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const applyPreset = (rk: RoleKey) => {
    setForm((prev) => ({ ...prev, roles: [rk], permissions: [...mergePermissions([rk])] }));
  };

  const togglePerm = (perm: Permission) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...BLANK, permissions: [...mergePermissions(['viewer'])] });
    setShowModal(true);
  };

  const openEdit = (s: StaffMember) => {
    setEditing(s);
    setForm({
      name: s.name, email: s.email, password: s.password,
      roles: [...s.roles],
      permissions: s.permissions ? [...s.permissions] : [...mergePermissions(s.roles)],
      status: s.status, department: s.department,
    });
    setShowModal(true);
  };

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    const member: StaffMember = {
      id: editing?.id || `staff_${Date.now()}`,
      name: form.name, email: form.email, password: form.password,
      roles: form.roles, permissions: form.permissions,
      status: form.status, department: form.department,
      createdAt: editing?.createdAt || new Date().toISOString().slice(0, 10),
      lastLogin: editing?.lastLogin,
    };
    const updated = editing ? staff.map((s) => s.id === editing.id ? member : s) : [...staff, member];
    setStaff(updated);
    saveStaff(updated);
    showToast(editing ? '人员信息已更新' : '工作人员已添加');
    setShowModal(false);
  };

  const toggleStatus = (id: string) => {
    const updated = staff.map((s) => s.id === id ? { ...s, status: s.status === 'active' ? 'inactive' : 'active' } as StaffMember : s);
    setStaff(updated);
    saveStaff(updated);
    showToast('状态已更新');
  };

  const handleDelete = (id: string) => {
    const updated = staff.filter((s) => s.id !== id);
    setStaff(updated);
    saveStaff(updated);
    setDeleteConfirm(null);
    showToast('已删除工作人员');
  };

  const toggleShowPassword = (id: string) => {
    setShowPasswords((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const canWrite = can('staff:write');
  const canDelete = can('staff:delete');

  if (!canView) return <NoPermission resource="工作人员管理" />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {toast && (
        <div className="fixed top-5 right-5 z-50 px-4 py-2.5 bg-[#18181B] text-white text-sm rounded-xl shadow-lg">{toast}</div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#18181B]">工作人员管理</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">共 {staff.length} 名成员 · 精细权限控制</p>
        </div>
        {canWrite && (
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 bg-[#18181B] text-white text-sm font-medium rounded-lg hover:bg-[#27272A] cursor-pointer whitespace-nowrap transition-colors">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line" /></div>添加人员
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none"><i className="ri-search-line text-[#9CA3AF] text-sm" /></div>
          <input type="text" placeholder="搜索姓名/邮箱..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none bg-white w-52" />
        </div>
        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}
          className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg bg-white focus:outline-none cursor-pointer">
          <option value="all">全部角色</option>
          {ROLE_ORDER.map((rk) => <option key={rk} value={rk}>{ROLES[rk].label}</option>)}
        </select>
        <span className="flex items-center text-xs text-[#9CA3AF] ml-auto">{filtered.length} 名成员</span>
      </div>

      {/* Staff Table */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#F3F4F6] bg-[#FAFAFA]">
                {['姓名', '邮箱', '部门', '角色预设', '权限数', '密码', '状态', '最近登录', '操作'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-medium text-[#9CA3AF] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-12 text-[#9CA3AF] text-sm">暂无数据</td></tr>
              ) : filtered.map((s) => {
                const isActive = s.status === 'active';
                const showPass = showPasswords.has(s.id);
                const effectivePerms = getEffectivePerms(s);
                const permCount = effectivePerms.length;
                const isExpanded = expandedPermId === s.id;
                return (
                  <Fragment key={s.id}>
                  <tr className={`border-b ${isExpanded ? 'border-[#F59E0B]/20 bg-amber-50/30' : 'border-[#F9FAFB] hover:bg-[#FAFAFA]'} transition-colors ${!isActive ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-[#F3F4F6] flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-[#6B7280]">{s.name.slice(0, 1)}</span>
                        </div>
                        <span className="font-medium text-[#18181B] text-sm">{s.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#6B7280] text-xs">{s.email}</td>
                    <td className="px-4 py-3 text-[#6B7280] text-xs">{s.department}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {s.roles.map((rk) => (
                          <span key={rk} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ROLES[rk]?.color ?? 'bg-gray-100 text-gray-600'}`}>
                            {ROLES[rk]?.label ?? rk}
                          </span>
                        ))}
                        {s.permissions && s.permissions.length > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F3F4F6] text-[#9CA3AF]">自定义</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => setExpandedPermId(isExpanded ? null : s.id)}
                        className="flex items-center gap-1 cursor-pointer hover:opacity-75 transition-opacity">
                        <span className="text-xs font-semibold text-[#F59E0B]">{permCount}</span>
                        <span className="text-[10px] text-[#9CA3AF]">项</span>
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={`text-[#9CA3AF] text-xs ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                        </div>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-mono text-[#6B7280]">{showPass ? s.password : '••••••••'}</span>
                        <button onClick={() => toggleShowPassword(s.id)} className="w-4 h-4 flex items-center justify-center text-[#9CA3AF] hover:text-[#6B7280] cursor-pointer">
                          <i className={showPass ? 'ri-eye-off-line text-xs' : 'ri-eye-line text-xs'} />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {isActive ? '在职' : '停用'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#9CA3AF] text-xs whitespace-nowrap">
                      {s.lastLogin ? new Date(s.lastLogin).toLocaleDateString('zh-CN') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {canWrite && <button onClick={() => openEdit(s)} className="px-2 py-1 text-[10px] font-medium text-[#374151] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded cursor-pointer whitespace-nowrap transition-colors">编辑</button>}
                        {canWrite && (
                          <button onClick={() => toggleStatus(s.id)} className={`px-2 py-1 text-[10px] font-medium rounded cursor-pointer whitespace-nowrap transition-colors ${isActive ? 'text-amber-700 bg-amber-50 hover:bg-amber-100' : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'}`}>
                            {isActive ? '停用' : '启用'}
                          </button>
                        )}
                        {canDelete && isSuperAdmin && (
                          <button onClick={() => setDeleteConfirm(s.id)} className="px-2 py-1 text-[10px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded cursor-pointer whitespace-nowrap transition-colors">删除</button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={9} className="px-5 py-4 bg-amber-50/30 border-b border-[#F59E0B]/10">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-shield-check-line text-[#F59E0B] text-sm" />
                          </div>
                          <span className="text-xs font-semibold text-[#374151]">{s.name} 的权限详情</span>
                          <span className="text-[10px] text-[#9CA3AF]">共 {permCount} 项有效权限</span>
                          {s.roles.includes('super_admin') && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">超管全权限</span>
                          )}
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {PERM_ROWS.map((row) => (
                            <div key={row.label} className="bg-white rounded-lg px-3 py-2.5 border border-[#F3F4F6]">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className={`${row.icon} text-[#9CA3AF] text-[10px]`} />
                                </div>
                                <span className="text-[10px] font-semibold text-[#374151]">{row.label}</span>
                              </div>
                              {(['view', 'write', 'delete', 'approve'] as const).map((col) => {
                                const perm = row.perms[col];
                                if (!perm) return null;
                                const granted = effectivePerms.includes(perm);
                                return (
                                  <div key={col} className={`flex items-center gap-1 text-[10px] mb-0.5 ${granted ? 'text-emerald-700' : 'text-[#D1D5DB]'}`}>
                                    <div className="w-3 h-3 flex items-center justify-center shrink-0">
                                      <i className={granted ? 'ri-checkbox-circle-fill text-emerald-500' : 'ri-close-circle-fill text-[#E5E7EB]'} />
                                    </div>
                                    {COL_LABELS[col]}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6] shrink-0">
              <h3 className="font-bold text-[#18181B]">{editing ? '编辑工作人员' : '添加工作人员'}</h3>
              <button onClick={() => setShowModal(false)} className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#6B7280] cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleSave} className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">姓名 *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">邮箱 *</label>
                  <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} required
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">登录密码</label>
                  <input type="text" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">部门</label>
                  <select value={form.department} onChange={(e) => setForm((p) => ({ ...p, department: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none cursor-pointer">
                    {DEPT_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">状态</label>
                  <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as 'active' | 'inactive' }))}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none cursor-pointer">
                    <option value="active">在职</option>
                    <option value="inactive">停用</option>
                  </select>
                </div>
              </div>

              {/* Permission Configuration */}
              <div>
                <div className="flex items-center gap-2 mb-2.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shield-user-line text-[#F59E0B] text-sm" />
                  </div>
                  <span className="text-xs font-semibold text-[#374151]">权限配置</span>
                  <span className="text-[10px] text-[#9CA3AF]">可按模块独立配置，或选择快捷预设一键填充</span>
                </div>

                {/* Quick Presets */}
                <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 mb-3 bg-[#F9FAFB] rounded-xl border border-[#F3F4F6]">
                  <span className="text-[10px] text-[#9CA3AF] font-medium whitespace-nowrap">快捷预设：</span>
                  {ROLE_ORDER.map((rk) => {
                    const roleDef = ROLES[rk];
                    const rolePerms = mergePermissions([rk]);
                    const isActive = form.roles.includes(rk)
                      && rolePerms.length === form.permissions.length
                      && rolePerms.every((p) => form.permissions.includes(p));
                    return (
                      <button key={rk} type="button" onClick={() => applyPreset(rk)}
                        className={`text-[10px] px-2.5 py-1 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                          isActive ? `${roleDef.color} ring-2 ring-offset-1 ring-[#F59E0B]/60` : 'bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]'
                        }`}>
                        {roleDef.label}
                      </button>
                    );
                  })}
                  <span className="ml-auto text-[10px] text-[#9CA3AF]">已选 <span className="text-[#F59E0B] font-semibold">{form.permissions.length}</span> 项权限</span>
                </div>

                {/* Permission Matrix */}
                <div className="border border-[#E5E7EB] rounded-xl overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#FAFAFA] border-b border-[#F3F4F6]">
                        <th className="text-left px-4 py-2.5 text-[#374151] font-semibold">模块</th>
                        {(['view', 'write', 'delete', 'approve'] as const).map((col) => (
                          <th key={col} className="text-center px-3 py-2.5 text-[#9CA3AF] font-medium whitespace-nowrap">{COL_LABELS[col]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {PERM_ROWS.map((row, i) => (
                        <tr key={row.label} className={`border-b border-[#F9FAFB] last:border-0 ${i % 2 !== 0 ? 'bg-[#FAFAFA]/60' : ''}`}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <div className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className={`${row.icon} text-[#9CA3AF]`} />
                              </div>
                              <span className="text-[#374151] font-medium">{row.label}</span>
                            </div>
                          </td>
                          {(['view', 'write', 'delete', 'approve'] as const).map((col) => {
                            const perm = row.perms[col];
                            if (!perm) {
                              return <td key={col} className="text-center px-3 py-2.5"><span className="text-[#E5E7EB] text-sm">—</span></td>;
                            }
                            const checked = form.permissions.includes(perm);
                            return (
                              <td key={col} className="text-center px-3 py-2.5">
                                <button type="button" onClick={() => togglePerm(perm)}
                                  className={`w-4 h-4 rounded flex items-center justify-center mx-auto cursor-pointer transition-all ${
                                    checked ? 'bg-[#F59E0B]' : 'border-2 border-[#D1D5DB] hover:border-[#F59E0B]'
                                  }`}>
                                  {checked && <i className="ri-check-line text-white" style={{ fontSize: '8px' }} />}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex gap-3 pt-2 border-t border-[#F3F4F6]">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg cursor-pointer whitespace-nowrap transition-colors">
                  取消
                </button>
                <button type="submit"
                  className="flex-1 py-2.5 text-sm font-medium text-white bg-[#18181B] hover:bg-[#27272A] rounded-lg cursor-pointer whitespace-nowrap transition-colors">
                  {editing ? '保存修改' : '添加人员'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mb-4 mx-auto">
              <i className="ri-user-unfollow-line text-red-500 text-lg" />
            </div>
            <h3 className="font-bold text-[#18181B] text-center mb-2">确认删除</h3>
            <p className="text-sm text-[#6B7280] text-center mb-5">删除后该工作人员将无法访问后台，确认继续？</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] rounded-lg cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
